import type jsPDF from "jspdf";
import type { PdfTemplateDesign } from "@workspace/api-zod/pdf-template";
import type { CompanyInfo, QuoteData } from "./pdf-generator";

import { fitImage, text, hexRgb, tintRgb, type PdfImage } from "./pdf-template-drawing";
import { drawFullPageCover, drawFullPageClosing } from "./pdf-template-full-page";
import { titleFontFamily } from "./pdf-template-fonts";
export { hexRgb, tintRgb } from "./pdf-template-drawing";
export type { PdfImage } from "./pdf-template-drawing";

function decoration(doc: jsPDF, design: PdfTemplateDesign, closing: boolean) {
  const primary = hexRgb(design.primaryColor), accent = hexRgb(design.accentColor);
  if (design.layout === "bold") {
    doc.setFillColor(...primary); doc.rect(0, 0, 210, closing ? 42 : 15, "F");
    doc.setFillColor(...accent); doc.rect(0, 277, 210, 20, "F");
  } else if (design.layout === "architect") {
    doc.setDrawColor(...accent); doc.setLineWidth(0.5);
    doc.rect(10, 10, 190, 274);
    doc.line(14, 20, 54, 20); doc.line(180, 260, 196, 260);
    doc.setFillColor(...primary); doc.rect(10, 10, 4, 56, "F");
  } else {
    doc.setFillColor(...primary); doc.rect(0, 0, 210, 7, "F");
    doc.setFillColor(...accent); doc.rect(20, 278, 40, 2, "F");
  }
}

export function drawTemplateCover(doc: jsPDF, quote: QuoteData, company: CompanyInfo | undefined, design: PdfTemplateDesign,
  images: { logo: PdfImage | null; artwork: PdfImage | null }, formattedTotal: string) {
  if (design.pageVersion === 2 || design.pageVersion === 3 || design.pageVersion === 4 || design.pageVersion === 5 || design.pageVersion === 6) {
    doc.saveGraphicsState();
    drawFullPageCover(doc, quote, company, design, images, formattedTotal);
    doc.restoreGraphicsState();
    return;
  }
  decoration(doc, design, false);
  if (images.logo) fitImage(doc, images.logo, 20, 20, 68, 25, design.logoAlignment);
  doc.setTextColor(...hexRgb(design.primaryColor));
  if (!images.logo || (design.showCompanyName ?? company?.showCompanyName) !== false) text(doc, company?.name || "Your Company", images.logo ? 98 : 20, 33, images.logo ? 92 : 170, design.companyNameSize ?? 13, 2, true,
    design.companyNameFont === "title" ? titleFontFamily(design) : "helvetica");
  doc.setTextColor(...hexRgb(design.accentColor));
  text(doc, "QUOTATION", 20, 62, 170, 10, 1, true);
  doc.setTextColor(...hexRgb(design.primaryColor));
  text(doc, design.coverTitle, 20, 80, 170, 29, 3, true);
  doc.setTextColor(80, 80, 80);
  text(doc, design.coverSubtitle, 20, 129, 170, 11, 4);
  if (images.artwork) {
    fitImage(doc, images.artwork, 20, 155, 170, 64);
  } else {
    doc.setFillColor(...tintRgb(design.accentColor)); doc.rect(20, 155, 170, 62, "F");
    doc.setDrawColor(...hexRgb(design.accentColor)); doc.setLineWidth(0.7);
    doc.line(30, 207, 80, 167); doc.line(80, 167, 126, 207);
    doc.line(80, 167, 162, 167); doc.line(162, 167, 180, 186);
    doc.line(126, 207, 180, 186); doc.line(80, 167, 80, 207);
  }
  doc.setTextColor(90, 90, 90);
  text(doc, "PREPARED FOR", 20, 233, 110, 8, 1, true);
  doc.setTextColor(...hexRgb(design.primaryColor));
  text(doc, quote.client?.name || quote.projectType || "Your project", 20, 242, 105, 14, 2, true);
  doc.setTextColor(80, 80, 80);
  const projectType = quote.projectType?.replace(/_/g, " ").replace(/\b\w/g, letter => letter.toUpperCase());
  text(doc, [projectType, quote.projectAddress].filter(Boolean).join("\n"), 20, 259, 105, 9, 3);
  doc.setTextColor(...hexRgb(design.primaryColor));
  text(doc, quote.quoteNumber, 137, 233, 53, 10, 2, true);
  const date = quote.createdAt ? new Date(quote.createdAt) : null;
  if (date && !Number.isNaN(date.getTime())) text(doc, date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }), 137, 248, 53, 9, 1);
  doc.setTextColor(90, 90, 90);
  text(doc, quote.includeVat ? "TOTAL INCLUDING VAT" : "TOTAL", 137, 259, 53, 7, 1, true);
  doc.setTextColor(...hexRgb(design.primaryColor));
  text(doc, formattedTotal, 137, 269, 53, 13, 1, true);
}

export function drawTemplateClosing(doc: jsPDF, company: CompanyInfo | undefined, design: PdfTemplateDesign,
  images: { logo: PdfImage | null; artwork: PdfImage | null }) {
  if (design.pageVersion === 2 || design.pageVersion === 3 || design.pageVersion === 4 || design.pageVersion === 5 || design.pageVersion === 6) {
    doc.saveGraphicsState();
    drawFullPageClosing(doc, company, design, images);
    doc.restoreGraphicsState();
    return;
  }
  decoration(doc, design, true);
  if (images.artwork) fitImage(doc, images.artwork, 20, 18, 170, 45);
  if (images.logo) fitImage(doc, images.logo, 20, 69, 68, 21);
  doc.setTextColor(...hexRgb(design.primaryColor));
  text(doc, design.thankYouTitle, 20, 108, 170, 29, 3, true);
  doc.setTextColor(70, 70, 70);
  text(doc, design.thankYouMessage, 20, 157, 170, 11, 15);
  doc.setTextColor(...hexRgb(design.accentColor));
  doc.setLineWidth(0.5); doc.setDrawColor(...hexRgb(design.accentColor)); doc.line(20, 240, 190, 240);
  doc.setTextColor(...hexRgb(design.primaryColor));
  text(doc, company?.name || "Your Company", 20, 250, 170, 12, 1, true);
  doc.setTextColor(80, 80, 80);
  text(doc, [company?.phone, company?.email, company?.website].filter(Boolean).join("\n"), 20, 260, 170, 9, 3);
}
